import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import { shippingBucketForCountry } from "../../shippingCountries";
import {
  CurrencyCode,
  DISCOUNT_PERCENT,
  PER_DEVICE_VALUE_CENTS,
  PLAN_CONTENT,
  PlanContent,
  PlanId,
} from "../PlanBillingStep/constants";
import { resolveSelection } from "../PlanBillingStep/selection";
import { useOnboardingPlansContext } from "../PlanBillingStep/useOnboardingPlans";
import { ShippingRate, shippingAmountFor } from "./shippingRates";
import { useShippingRates } from "./useShippingRates";

/**
 * The single pricing computation for the Pixi Bundle purchase, shared by the
 * Checkout step (which shows the shipping-inclusive grand total) and the
 * Complete step (which shows the subscription total and shipping separately).
 * Keeping it in one place stops the two screens' numbers from drifting.
 *
 * All amounts are in MINOR units (cents), matching the billing currency util.
 * When the `createCustomer` response becomes the source of truth (GRWBKK-1662),
 * repoint the totals here rather than in each screen.
 */
export interface CheckoutPricing {
  planId: PlanId;
  planContent: PlanContent;
  currency: CurrencyCode;
  billingCycle: "year" | "month";
  licenses: number;
  shippingCountry: string;
  isFreeDevice: boolean;
  isDiscount: boolean;
  licencesCents: number;
  deviceSavingsCents: number;
  discountCents: number;
  shippingCents: number;
  /** Licences minus any discount — EXCLUDES shipping. */
  subscriptionTotalCents: number;
  /** Subscription total plus shipping (free-device deal only). */
  grandTotalCents: number;
  rates: ShippingRate[];
}

export const useCheckoutPricing = (
  state: PixiWizardSelection,
): CheckoutPricing => {
  const { getPlan, isProTrialist } = useOnboardingPlansContext();
  const { rates } = useShippingRates();

  const { planId, currency, billingCycle, shippingCountry, licenses } =
    resolveSelection(state, isProTrialist);
  const plan = getPlan(planId, billingCycle, currency);

  const isFreeDevice = state.dealId === "free-device";
  const isDiscount = state.dealId === "discount";

  const licencesCents = plan ? plan.price * licenses : 0;
  const deviceSavingsCents = PER_DEVICE_VALUE_CENTS[currency] * licenses;
  const discountCents = Math.round((licencesCents * DISCOUNT_PERCENT) / 100);
  // `shippingCountry` is an ISO code from the dropdown; the API keys rates by
  // regional bucket, so translate before looking up the rate (see GRWBKK SC-08).
  const shippingCents = shippingAmountFor(rates, {
    countryBucket: shippingBucketForCountry(shippingCountry),
    currency,
    screenCount: licenses,
    shippingService: state.shippingService ?? "standard",
  });

  const subscriptionTotalCents =
    licencesCents - (isDiscount ? discountCents : 0);
  const grandTotalCents =
    subscriptionTotalCents + (isFreeDevice ? shippingCents : 0);

  return {
    planId,
    planContent: PLAN_CONTENT[planId],
    currency,
    billingCycle,
    licenses,
    shippingCountry,
    isFreeDevice,
    isDiscount,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    subscriptionTotalCents,
    grandTotalCents,
    rates,
  };
};
